import { useMemo, useState } from "react";
import { Button, Card, message, Modal } from "antd";
import { useRequest } from "ahooks";
import { EditForm } from "../../../components/editForm";
import { Table, DEFAULT_PAGE_DATA } from "../../../components/table";
import { GlobalPayServer } from "../../../server";
import { DEFAULT_QUERY_DATA, generateQueryFormConfig, generateTableColumns, orderStatusText } from "./config";
import dayjs from "dayjs";

const server = GlobalPayServer.PayOrder;

const exportColumns = [
    { title: "ID", render: (record: any) => record.Id },
    { title: "订单号", render: (record: any) => record.OrderNo },
    { title: "用户ID", render: (record: any) => record.MemberId },
    { title: "币种金额", render: (record: any) => `${record.CurrencyAmount || 0} ${record.CurrencyCode || ""}` },
    { title: "USD金额", render: (record: any) => record.UsdAmount },
    { title: "服务费", render: (record: any) => record.ServiceFee },
    { title: "通道费", render: (record: any) => record.ChannelFee },
    { title: "总USD金额", render: (record: any) => record.TotalUsdAmount },
    { title: "状态", render: (record: any) => orderStatusText(record.Status) },
    { title: "上游预订单号", render: (record: any) => record.UpstreamPreOrderNo },
    { title: "上游订单号", render: (record: any) => record.UpstreamOrderNo },
    { title: "上游错误", render: (record: any) => record.UpstreamErrMsg },
    { title: "银行名称", render: (record: any) => record.BankName },
    { title: "银行账号", render: (record: any) => record.BankAccountNumber },
    { title: "创建时间", render: (record: any) => record.CreatedAtString },
    { title: "更新时间", render: (record: any) => record.UpdatedAtString },
];

const escapeExcelCell = (value: any) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const PayOrderList = () => {
    const [loading, setLoading] = useState<boolean>(false);
    const [queryData, setQueryData] = useState<any>({ ...DEFAULT_QUERY_DATA });
    const [tableData, setTableData] = useState<any[]>([]);
    const [pageData, setPageData] = useState<any>({ ...DEFAULT_PAGE_DATA });

    const normalizePageData = (page?: number, pageSize?: number, total?: number) => ({
        Page: page || pageData.Page || DEFAULT_PAGE_DATA.Page,
        PageSize: pageSize || pageData.PageSize || DEFAULT_PAGE_DATA.PageSize,
        Total: total ?? pageData.Total ?? DEFAULT_PAGE_DATA.Total,
    });

    const exportOrderList = () => {
        if (!tableData.length) {
            message.warning("暂无可导出的订单数据");
            return;
        }

        const header = exportColumns.map(column => `<th>${escapeExcelCell(column.title)}</th>`).join("");
        const body = tableData.map(record => (
            `<tr>${exportColumns.map(column => (
                `<td style='mso-number-format:"\\@";'>${escapeExcelCell(column.render(record))}</td>`
            )).join("")}</tr>`
        )).join("");
        const worksheet = `
            <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">
                <head>
                    <meta charset="UTF-8" />
                    <!--[if gte mso 9]>
                    <xml>
                        <x:ExcelWorkbook>
                            <x:ExcelWorksheets>
                                <x:ExcelWorksheet>
                                    <x:Name>支付订单列表</x:Name>
                                    <x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions>
                                </x:ExcelWorksheet>
                            </x:ExcelWorksheets>
                        </x:ExcelWorkbook>
                    </xml>
                    <![endif]-->
                </head>
                <body><table border="1"><thead><tr>${header}</tr></thead><tbody>${body}</tbody></table></body>
            </html>
        `;
        const blob = new Blob(["\ufeff", worksheet], { type: "application/vnd.ms-excel;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `支付订单列表_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.xls`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
    };

    const { run, refresh } = useRequest(server.list, {
        defaultParams: [{ ...pageData }],
        onBefore: () => setLoading(true),
        onFinally: () => setLoading(false),
        onError: () => {
            setTableData([]);
            setPageData({ ...pageData, Total: 0 });
        },
        onSuccess: (res: any) => {
            const { Total, List, Page, PageSize } = res;
            setTableData(List || []);
            setPageData(normalizePageData(Page, PageSize, Total));
        }
    });

    const { run: runAudit, loading: auditLoading } = useRequest(server.audit, {
        manual: true,
        onSuccess: () => {
            message.success("操作成功");
            refresh();
        },
        onError: (error: any) => {
            message.error("操作失败: " + (error?.message || ""));
        }
    });

    const queryFormConfig = useMemo(() => generateQueryFormConfig(), []);
    const tableColumns = useMemo(() => [
        ...generateTableColumns(),
        {
            title: "操作",
            key: "action",
            fixed: "right",
            width: 170,
            render: (_: any, record: any) => {
                const status = Number(record.Status);
                if (status === 20 || status === 40) return null;

                return (
                    <div style={{ display: "flex", gap: 8 }}>
                        <Button
                            type="link"
                            size="small"
                            loading={auditLoading}
                            onClick={() => {
                                Modal.confirm({
                                    title: "确认补强为成功",
                                    content: `确定将订单"${record.OrderNo}"标记为成功吗？`,
                                    onOk: () => runAudit({ Id: record.Id, Status: 40 })
                                });
                            }}
                        >
                            标记成功
                        </Button>
                        <Button
                            type="link"
                            danger
                            size="small"
                            loading={auditLoading}
                            onClick={() => {
                                Modal.confirm({
                                    title: "确认补强为失败",
                                    content: `确定将订单"${record.OrderNo}"标记为失败吗？`,
                                    onOk: () => runAudit({ Id: record.Id, Status: 20 })
                                });
                            }}
                        >
                            标记失败
                        </Button>
                    </div>
                );
            }
        }
    ], [auditLoading, runAudit]);

    return (
        <div>
            <Card>
                <div className="flex flex-between items-end">
                    <EditForm
                        componentId="pay-order-query-form"
                        loading={loading}
                        formAttrsProps={{ layout: "inline" }}
                        formData={queryData}
                        onDataChange={setQueryData}
                        formConfig={queryFormConfig}
                        onQuery={() => run({ ...DEFAULT_PAGE_DATA, ...queryData })}
                        onReset={() => {
                            setQueryData({ ...DEFAULT_QUERY_DATA });
                            run({ ...DEFAULT_QUERY_DATA, ...DEFAULT_PAGE_DATA });
                        }}
                    />
                    <Button className="ml10" type="primary" loading={loading} onClick={exportOrderList}>
                        导出
                    </Button>
                </div>
            </Card>
            <Card className="mt20">
                <Table
                    loading={loading}
                    dataSource={tableData}
                    columns={tableColumns}
                    rowKey="Id"
                    scroll={{ x: "max-content" }}
                    page={pageData}
                    onPageChange={(Page: number, PageSize: number) => {
                        const currentPage = normalizePageData(Page, PageSize, pageData.Total);
                        run({ ...currentPage, ...queryData });
                    }}
                />
            </Card>
        </div>
    );
}
